import { useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { dietSettings, stepConfigItem } from '../types';
import { dietSettingsIsLastStep } from '../utils/DietSettingsIsLastStep';
import { dietSettingsGetNextStep } from '../utils/DietSettingsNextStepAction';
import { dietSettingsGetPreviousStep } from '../utils/DietSettingsPreviousStepAction';
import { dietSettingsGetSchemaForParameters } from '../utils/DietSettingsSchemaForParametersAction';
import StepContext from './StepContext';
import { DietIdEnum, DietModeType } from '@front-activerse/common-components';

interface StepProviderProps {
  currentMainStep: string;
  schema: stepConfigItem[];
  children: React.ReactNode;
  dietTypes: {
    id: number;
    value: DietIdEnum;
    optionTitle: string;
    optionDescription: string;
    macro?: {
      protein: number;
      fat: number;
      carbs: number;
    };
  }[];
  dietLevels: {
    id: number;
    value: DietIdEnum;
    optionTitle: string;
    optionDescription: string;
    macro?: {
      protein: number;
      fat: number;
      carbs: number;
    };
  }[];
  dietModes?: {
    value: DietModeType;
    optionTitle: string;
    optionDescription: string;
  }[];
  generateDiet: (values: dietSettings) => void;
  saveUserData: (values: dietSettings) => void;
  validateDietSettings: (values: dietSettings) => void;
}

const StepProvider: React.FC<StepProviderProps> = ({
  currentMainStep,
  schema,
  children,
  dietTypes,
  dietLevels,
  dietModes,
  generateDiet,
  saveUserData,
  validateDietSettings,
}) => {
  const params = useParams();
  const stepFromUrl = params['*'];
  const { values: dietSettings } = useFormikContext<dietSettings>();
  const schemaForParameters = dietSettingsGetSchemaForParameters(schema, dietSettings);

  const [currentStep, setCurrentStep] = useState(
    schemaForParameters.find((step) => step.slug === stepFromUrl)?.id || ''
  );

  useEffect(() => {
    setCurrentStep(schemaForParameters.find((step) => step.slug === stepFromUrl)?.id || '');
  }, [params, stepFromUrl, schema, schemaForParameters]);

  const prevStep = dietSettingsGetPreviousStep(schemaForParameters, dietSettings, currentStep);
  const nextStep = dietSettingsGetNextStep(schemaForParameters, dietSettings, currentStep);
  const isLastStep = dietSettingsIsLastStep(schema, dietSettings, currentStep);
  const shouldBackendValidate = schemaForParameters.find(
    (step) => step.slug === stepFromUrl
  )?.shouldBackendValidate;

  return (
    <StepContext.Provider
      value={{
        currentStep: currentStep,
        currentMainStep: currentMainStep,
        schema: schema,
        prevStep: prevStep,
        nextStep: nextStep,
        isLastStep: isLastStep,
        shouldBackendValidate: shouldBackendValidate,
        dietTypes: dietTypes,
        dietLevels: dietLevels,
        dietModes: dietModes,
        generateDiet: generateDiet,
        saveUserData: saveUserData,
        validateDietSettings: validateDietSettings,
      }}
    >
      {children}
    </StepContext.Provider>
  );
};

export default StepProvider;
